import { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface CustomerFlow {
  newThisQuarter: number;
  active: number;
  churned: number;
  expansion: number;
}

/**
 * Story 57.3 — customer flow. New / active / churn / expansion counters.
 */
export function CustomerFlowWidget() {
  const [data, setData] = useState<CustomerFlow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<CustomerFlow>('/analytics/customer-flow')
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading customer flow…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Heart className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Customer flow
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="New this quarter" value={data.newThisQuarter} accent="text-emerald-300" />
        <Tile label="Active" value={data.active} />
        <Tile label="Churned" value={data.churned} accent="text-rose-300" />
        <Tile label="Expansion" value={data.expansion} accent="text-blue-300" />
      </div>
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="rounded-md bg-muted/40 p-3 text-center">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent ?? ''}`}>{value}</div>
    </div>
  );
}
